'use client';

import dayjs from 'dayjs';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useAppContext } from 'src/context/app';
import { ärAllaId } from 'src/context/app/alla-organisationer';
import { useAuthContext } from 'src/auth/hooks';
import { useMenyInställningar } from 'src/hooks/use-meny-installningar';

import { tillAttGöraPost, STANDARDINSTÄLLNINGAR } from './AttGoraTyper';
import {
  skriv,
  TOM_LISTA,
  läsLista,
  NYCKEL_KLARA,
  läsInställningar,
  NYCKEL_INSTÄLLNINGAR,
} from './lagring';
import { bedömArbetsgivare } from './arbetsgivarprofil';
import { byggSondering } from './sonder';
import { väljIAppenSedan } from './i-appen-sedan';
import { tillEgenkontrollpost } from './egenkontroll-poster';
import {
  toDayjs,
  beräknaMyndighetsuppgifter,
  STANDARD_HISTORIK_DAGAR,
  STANDARD_ÅRSHORISONT_DAGAR,
} from './myndighetsdatum';
import { hämtaAvklarat, INGET_AVKLARAT } from './AttGoraAvklarat';
import { hämtaTaxeringsstatus, INGEN_TAXERING } from './FastighetsdeklarationStatus';
import { HämtaAllaUnderlag } from '../inkop/InkopService';
import { HämtaLöntagare } from '../loner/LoneService';
import { hämtaAllaKundfakturorInklBokförda } from '../bokforing/affarshandelser/skapa/kundfaktura/KundfakturaService';
import { hämtaEgenkontrollÖversikt } from '../fastighet/egenkontroll/EgenkontrollService';

import type { Avklarat } from './AttGoraAvklarat';
import type { Taxeringsstatus } from './FastighetsdeklarationStatus';
import type { UppgiftsTyp, Deklarationsprofil } from './myndighetsdatum';
import type { AttGöraPost, AttGöraInställningar } from './AttGoraTyper';
import type { IEgenkontrolluppgift } from '../fastighet/egenkontroll/EgenkontrollService';
import type { IOrganisation } from 'src/types/nya/common';

// ----------------------------------------------------------------------

/**
 * Fönstret som fakturor och egenkontroll filtreras till. Det speglar regelmotorns
 * *yttre* gräns, inte den korta horisonten för de återkommande myndighetsserierna:
 * en faktura är en engångspost som inte staplas, och ska behandlas som de årsbundna
 * uppgifterna. Värdena importeras i stället för att skrivas av, så de inte kan glida
 * isär från `beräknaMyndighetsuppgifter`.
 */
const HISTORIK_DAGAR = STANDARD_HISTORIK_DAGAR;
const HORISONT_DAGAR = STANDARD_ÅRSHORISONT_DAGAR;

/**
 * Samlar allt organisationen behöver göra gentemot myndigheter och motparter.
 *
 * Myndighetsdatumen räknas ut lokalt av regelmotorn i `myndighetsdatum.ts`.
 * Övriga källor läggs till här; varje källa mappas till en `AttGöraPost`.
 */
export function useAttGöra(förValdFörening?: IOrganisation) {
  // Myndighetsdatumen och de lokala avbockningarna hör till valet som sådant —
  // hubbläget har egna deklarationsdatum (personens egna) och egna inställningar,
  // och är därför `valdOrganisation`. Hämtningarna längre ned gäller en riktig
  // förening och gatear på `föreningsId`, som är undefined i hubbläget.
  const { organisation: valdFörening, valdOrganisation: valtLäge } = useAppContext();

  // `förValdFörening` låter hubben läsa en annan förening än den som är vald i
  // menyn — den renderar en sond per förening för att kunna visa allas
  // förfallodagar i samma lista. En hook går inte att loopa, så organisationen
  // måste in som argument.
  //
  // Är argumentet satt är det en riktig förening, och då gäller den för både
  // regelmotorn och hämtningarna. Bindningen till `orgId` längre ned är därmed
  // oförändrad: allt state hör fortfarande ihop med den organisation det lästes
  // för, vilket är det som hindrar en förenings avbockningar från att visas för
  // en annan.
  const organisation = förValdFörening ?? valtLäge;
  const förening = förValdFörening ?? valdFörening;
  const orgId = organisation?.id;
  const föreningsId = förening?.id;

  const { user, anvandare } = useAuthContext();

  /**
   * Vad den här organisationen alls får frågas ut om.
   *
   * Listan hämtade tidigare varje sekundär källa vid varje sidladdning, oavsett om
   * organisationen hade modulen påslagen. För en förening på lanseringsytan var merparten
   * parkerade moduler som ändå inte kan svara med något, och skatteytornas egen rate
   * limiting slog till med 429 under vanlig navigering. Regeln bor i `sonder.ts`, där det
   * också står varför en avstängd sond måste dölja sin uppgift och inte bara tystna.
   */
  // Inte i hubbläget: där är `orgId` sentinelvärdet 'alla', och en hämtning på det ger 400
  // från backend som väntar sig en UUID. Hubben har ingen egen meny att fråga om — dess
  // sonder renderas som en `AttGöraSond` per förening, var och en med sin riktiga orgId.
  const { inställningar: menyval, klar: menyvalKlara } = useMenyInställningar(
    ärAllaId(orgId) ? undefined : orgId
  );
  const sondering = useMemo(
    () => byggSondering(menyval, menyvalKlara),
    [menyval, menyvalKlara]
  );
  const { fakturor: sonderaFakturor, skatt: sonderaSkatt, lön: sonderaLön } = sondering;
  const { styrelse: sonderaStyrelse, fastighet: sonderaFastighet } = sondering;

  /** Föreningens dag, eller användarens i hubbläget — regeln står i `i-appen-sedan.ts`. */
  const iAppenSedan = useMemo(() => väljIAppenSedan(förening, anvandare), [förening, anvandare]);

  /**
   * Allt organisationsberoende state hålls ihop med den orgId det lästes för.
   * Utan den bindningen visas förra organisationens avbockningar och
   * inställningar under renderingarna innan effekten hunnit läsa om — och
   * uppgifts-id:n som `agd-2026-07` är inte organisationsunika.
   */
  const [lagrat, setLagrat] = useState<{
    orgId?: string;
    inställningar: AttGöraInställningar;
    klara: string[];
  }>({ inställningar: STANDARDINSTÄLLNINGAR, klara: [] });

  const gällerAktuellOrg = lagrat.orgId === orgId;
  const inställningar = gällerAktuellOrg ? lagrat.inställningar : STANDARDINSTÄLLNINGAR;
  const klara = gällerAktuellOrg ? lagrat.klara : TOM_LISTA;

  const [fakturaposter, setFakturaposter] = useState<AttGöraPost[]>([]);
  const [egenkontrollposter, setEgenkontrollposter] = useState<AttGöraPost[]>([]);
  /**
   * Organisationens egna uppgifter — avbockningar och antal löntagare — bärs
   * med sitt orgId av samma skäl som `lagrat` ovan. Utan bindningen lever förra
   * organisationens svar kvar tills det nya kommit in, och arbetsgivar-
   * bedömningen skulle då kunna slå om till fel håll i ett par renderingar.
   */
  const [hämtat, setHämtat] = useState<{
    orgId?: string;
    avklarat: Avklarat;
    antalLöntagare?: number;
  }>({ avklarat: INGET_AVKLARAT });

  const gällerHämtat = hämtat.orgId === orgId;
  const avklarat = gällerHämtat ? hämtat.avklarat : INGET_AVKLARAT;
  const antalLöntagare = gällerHämtat ? hämtat.antalLöntagare : undefined;

  /**
   * Om den inloggade äger ett småhus och ska fastighetsdeklarera.
   *
   * Bunden till användaren och inte till orgId, till skillnad från allt annat här: ett småhus ägs
   * av en fysisk person, och svaret gäller personen oavsett vilken förening som är vald.
   */
  const [taxering, setTaxering] = useState<Taxeringsstatus>(INGEN_TAXERING);

  const [fakturaAvklarat, setFakturaAvklarat] = useState<Record<string, string>>({});
  /** Bumpas för att tvinga omhämtning, t.ex. när panelen öppnas. */
  const [omhämtning, setOmhämtning] = useState(0);
  const uppdatera = useCallback(() => setOmhämtning((n) => n + 1), []);

  // En ny dag ska ändra "3 dagar kvar" utan omladdning.
  const [idag, setIdag] = useState(() => dayjs().startOf('day'));
  useEffect(() => {
    const timer = setInterval(() => {
      setIdag((tidigare) => {
        const nu = dayjs().startOf('day');
        return nu.isSame(tidigare, 'day') ? tidigare : nu;
      });
    }, 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setLagrat({ orgId: undefined, inställningar: STANDARDINSTÄLLNINGAR, klara: TOM_LISTA });
      return;
    }
    setLagrat({
      orgId,
      inställningar: läsInställningar(`${NYCKEL_INSTÄLLNINGAR}:${orgId}`),
      klara: läsLista(`${NYCKEL_KLARA}:${orgId}`),
    });
  }, [orgId]);

  /** Skriver bara när state redan tillhör den aktuella organisationen. */
  const ändraInställningar = useCallback(
    (beräkna: (tidigare: AttGöraInställningar) => AttGöraInställningar) => {
      if (!orgId) return;
      setLagrat((tidigare) => {
        const bas = tidigare.orgId === orgId ? tidigare.inställningar : STANDARDINSTÄLLNINGAR;
        const nya = beräkna(bas);
        skriv(`${NYCKEL_INSTÄLLNINGAR}:${orgId}`, nya);
        return { orgId, inställningar: nya, klara: tidigare.orgId === orgId ? tidigare.klara : TOM_LISTA };
      });
    },
    [orgId]
  );

  const uppdateraInställningar = useCallback(
    (ändring: Partial<AttGöraInställningar>) =>
      ändraInställningar((tidigare) => ({ ...tidigare, ...ändring })),
    [ändraInställningar]
  );

  const växlaTyp = useCallback(
    (typ: UppgiftsTyp) =>
      ändraInställningar((tidigare) => ({
        ...tidigare,
        dolda: tidigare.dolda.includes(typ)
          ? tidigare.dolda.filter((t) => t !== typ)
          : [...tidigare.dolda, typ],
      })),
    [ändraInställningar]
  );

  const växlaKlar = useCallback(
    (id: string) => {
      if (!orgId) return;
      setLagrat((tidigare) => {
        const bas = tidigare.orgId === orgId ? tidigare.klara : TOM_LISTA;
        const nya = bas.includes(id) ? bas.filter((k) => k !== id) : [...bas, id];
        skriv(`${NYCKEL_KLARA}:${orgId}`, nya);
        return {
          orgId,
          inställningar: tidigare.orgId === orgId ? tidigare.inställningar : STANDARDINSTÄLLNINGAR,
          klara: nya,
        };
      });
    },
    [orgId]
  );

  // Fakturor att agera på. Två källor, båda sekundära: ett fel här får aldrig
  // hindra myndighetsdatumen från att visas.
  useEffect(() => {
    let avbruten = false;
    if (!föreningsId || !sonderaFakturor) {
      setFakturaposter([]);
      setFakturaAvklarat({});
      return undefined;
    }

    /** Obetalda kundfakturor — en påminnelse ska skickas. */
    const kundfakturor = hämtaAllaKundfakturorInklBokförda(föreningsId).then((svar) =>
      ((Array.isArray(svar) ? svar : []) as any[])
        .filter((f) => !f?.matchadBetalning)
        .map((f, index): AttGöraPost | null => {
          const förfallodag = toDayjs(f?.förfalloDag);
          if (!förfallodag) return null;
          return {
            id: `kundfaktura-${f?.fakturaReferens ?? index}-${förfallodag.format('YYYYMMDD')}`,
            titel: `Obetald kundfaktura ${f?.fakturaReferens ?? ''}`.trim(),
            detalj: f?.kund?.namn ? `Kund: ${f.kund.namn}` : 'Skicka påminnelse.',
            källa: 'FAKTURA',
            avsändare: 'Kundfaktura',
            förfallodag,
            belopp: typeof f?.beloppInklMoms === 'number' ? f.beloppInklMoms : undefined,
          };
        })
        .filter((p): p is AttGöraPost => p !== null)
    );

    /**
     * Leverantörsfakturor. Ett bokfört underlag betyder att betalningen är matchad
     * och slutförd, alltså att fakturan är betald — den posten avklaras automatiskt
     * i stället för att tyst försvinna ur listan. Förfallodagen kommer ur
     * AI-tolkningen; fakturor utan förfallodag hoppas över hellre än att gissas.
     */
    const leverantörsfakturor = HämtaAllaUnderlag(föreningsId).then((svar) => {
      const poster: AttGöraPost[] = [];
      const betalda: Record<string, string> = {};

      ((Array.isArray(svar) ? svar : []) as any[]).forEach((underlag, index) => {
        const info = underlag?.inköpTolkning?.fakturainformationTolkning;
        const förfallodag = toDayjs(info?.förfalloDag);
        if (!förfallodag) return;

        const id = `levfaktura-${underlag?.id ?? index}`;
        const leverantör = info?.leverantörsnamn ?? 'okänd leverantör';
        poster.push({
          id,
          titel: `Betala leverantörsfaktura – ${leverantör}`,
          detalj: info?.fakturanummer ? `Fakturanummer ${info.fakturanummer}` : undefined,
          källa: 'FAKTURA',
          avsändare: leverantör,
          förfallodag,
          belopp: typeof info?.totaltBelopp === 'number' ? info.totaltBelopp : undefined,
        });
        if (underlag?.bokförd) betalda[id] = 'Betald';
      });

      return { poster, betalda };
    });

    Promise.allSettled([kundfakturor, leverantörsfakturor]).then((resultat) => {
      if (avbruten) return;
      const kundposter = resultat[0].status === 'fulfilled' ? resultat[0].value : [];
      const lev = resultat[1].status === 'fulfilled' ? resultat[1].value : { poster: [], betalda: {} };
      setFakturaposter([...kundposter, ...lev.poster]);
      setFakturaAvklarat(lev.betalda);
    });

    return () => {
      avbruten = true;
    };
  }, [föreningsId, sonderaFakturor, omhämtning]);

  /**
   * Egenkontrollen: OVK, hiss, radon, brandskydd, skyddsrum, lekplats.
   *
   * Till skillnad från myndighetsdatumen räknas de inte ut här. Förfallodagen följer av det
   * senaste godkända protokollet, som bara backend känner, och av byggnadens
   * ventilationssystem — ett självdragshus ska ha OVK vart sjätte år och ett FTX-hus vart
   * tredje. En kopia av de reglerna i frontend hade varit fel i det ögonblick ett protokoll
   * registrerades.
   *
   * Sekundär källa som fakturorna: ett fel här får aldrig hindra myndighetsdatumen.
   */
  useEffect(() => {
    let avbruten = false;
    if (!föreningsId || !sonderaFastighet) {
      setEgenkontrollposter([]);
      return undefined;
    }

    hämtaEgenkontrollÖversikt(föreningsId)
      .then((uppgifter) => {
        if (avbruten) return;
        setEgenkontrollposter(uppgifter.map(tillEgenkontrollpost).filter(Boolean) as AttGöraPost[]);
      })
      .catch(() => {
        if (!avbruten) setEgenkontrollposter([]);
      });

    return () => {
      avbruten = true;
    };
  }, [föreningsId, sonderaFastighet, omhämtning]);

  /**
   * Vilka deklarationer som redan är bokförda/inlämnade, och hur många
   * löntagare organisationen har. Löntagarna används bara för att avgöra om
   * organisationen alls är arbetsgivare — en hämtning som fallerar lämnar
   * antalet okänt i stället för att räknas som noll.
   */
  useEffect(() => {
    let avbruten = false;
    // Ingen återställning behövs: `gällerHämtat` gör redan gammal data osynlig
    // så snart orgId inte längre matchar.
    if (!föreningsId) return undefined;

    Promise.allSettled([
      hämtaAvklarat(föreningsId, { skatt: sonderaSkatt, styrelse: sonderaStyrelse }),
      sonderaLön ? HämtaLöntagare(föreningsId) : Promise.resolve(undefined),
    ]).then(
      ([avklaratSvar, löntagarSvar]) => {
        if (avbruten) return;
        setHämtat({
          orgId,
          avklarat: avklaratSvar.status === 'fulfilled' ? avklaratSvar.value : INGET_AVKLARAT,
          antalLöntagare:
            löntagarSvar.status === 'fulfilled' && Array.isArray(löntagarSvar.value)
              ? löntagarSvar.value.length
              : undefined,
        });
      }
    );

    return () => {
      avbruten = true;
    };
  }, [föreningsId, orgId, sonderaSkatt, sonderaStyrelse, sonderaLön, omhämtning]);

  /**
   * Fastighetstaxeringen.
   *
   * Gateas på inloggad användare och inte på `föreningsId` — till skillnad från hämtningarna
   * ovan. Ett småhus ägs av en privatperson, som ofta står i Privatperson-läget och då saknar
   * förening. Just de användarna är hela målgruppen för påminnelsen, så en gating på föreningen
   * hade gjort funktionen osynlig för alla den är byggd för.
   */
  useEffect(() => {
    let avbruten = false;
    if (!user) {
      setTaxering(INGEN_TAXERING);
      return undefined;
    }

    hämtaTaxeringsstatus().then((status) => {
      if (!avbruten) setTaxering(status);
    });

    return () => {
      avbruten = true;
    };
  }, [user, omhämtning]);

  /**
   * Lokalt val vinner över organisationsregistret; 'INGEN' stänger av momsdatumen.
   *
   * Är organisationen inte momsregistrerad finns inga momsdatum alls — inte ens om en period
   * ligger kvar i registret från en tidigare registrering. Backend nekar deklarationen
   * (EjMomsregistreradException), och en påminnelse om något som inte går att göra är skräp.
   */
  const momsperiod = useMemo(() => {
    if (organisation?.momsregistrerad === false) {
      return undefined;
    }
    const val = inställningar.momsperiod ?? organisation?.momsDeklarationsPeriod;
    return val === 'INGEN' || !val ? undefined : val;
  }, [
    inställningar.momsperiod,
    organisation?.momsDeklarationsPeriod,
    organisation?.momsregistrerad,
  ]);

  /**
   * Arbetsgivardeklarationer hör bara hemma hos den som faktiskt är arbetsgivare.
   * Bedömningen görs ur organisationens egna uppgifter; användarens uttryckliga
   * val vinner över den, precis som för momsperioden.
   */
  const arbetsgivarbedömning = useMemo(
    () =>
      bedömArbetsgivare({
        organisationsTyp: organisation?.organisationsTyp,
        verksamhetsinriktning: organisation?.verksamhetsinriktning,
        antalLöntagare,
        harArbetsgivardeklarationer: avklarat.harArbetsgivardeklarationer,
      }),
    [
      organisation?.organisationsTyp,
      organisation?.verksamhetsinriktning,
      antalLöntagare,
      avklarat.harArbetsgivardeklarationer,
    ]
  );

  const ärArbetsgivare = inställningar.ärArbetsgivareVal ?? arbetsgivarbedömning.ärArbetsgivare;

  const profil = useMemo<Deklarationsprofil>(
    () => ({
      organisationsTyp: organisation?.organisationsTyp,
      räkenskapsår: organisation?.räkenskapsår,
      momsDeklarationsPeriod: momsperiod,
      ärArbetsgivare: organisation?.organisationsTyp === 'PRIVATPERSON' ? false : ärArbetsgivare,
      harDebiteradPreliminärskatt: inställningar.harDebiteradPreliminärskatt,
      lämnarDigitalt: inställningar.lämnarDigitalt,
      ägerSmåhus: taxering.aktuell,
      iAppenSedan,
    }),
    [organisation, inställningar, momsperiod, ärArbetsgivare, iAppenSedan, taxering.aktuell]
  );

  const poster = useMemo(() => {
    const myndighetsposter = beräknaMyndighetsuppgifter(profil, { idag })
      .filter((u) => !inställningar.dolda.includes(u.typ))
      // Samma regel som sonderingen: en uppgift vars modul är parkerad visas inte, eftersom
      // vi då inte heller frågar om den redan är gjord. Se `sonder.ts`.
      .filter((u) => sondering.visaUppgift(u.typ))
      .map(tillAttGöraPost);

    // Samma fönster som regelmotorn använder, så att betalda fakturor rullar av
    // listan i stället för att samlas på hög.
    const tidigast = idag.subtract(HISTORIK_DAGAR, 'day');
    const senast = idag.add(HORISONT_DAGAR, 'day');
    const fakturorIFönstret = fakturaposter.filter(
      (p) => !p.förfallodag.isBefore(tidigast, 'day') && !p.förfallodag.isAfter(senast, 'day')
    );

    // Egenkontrollen filtreras på samma fönster, med ett undantag: det som kräver åtgärd står
    // kvar hur gammalt det än är. En OVK som gick ut för två år sedan ska inte rulla av listan
    // för att den blev gammal — det är just då den är allvarlig.
    const egenkontrollIFönstret = egenkontrollposter.filter(
      (p) =>
        p.kräverÅtgärd ||
        (!p.förfallodag.isBefore(tidigast, 'day') && !p.förfallodag.isAfter(senast, 'day'))
    );

    return [...myndighetsposter, ...fakturorIFönstret, ...egenkontrollIFönstret].sort(
      (a, b) => a.förfallodag.valueOf() - b.förfallodag.valueOf()
    );
  }, [profil, fakturaposter, egenkontrollposter, inställningar.dolda, sondering, idag]);

  /**
   * En uppgift är klar antingen för att den bevisligen är utförd (bokförd
   * arbetsgivardeklaration, inlämnad momsdeklaration) eller för att den bockats
   * av för hand. Den härledda vägen väger tyngst och går inte att kryssa bort.
   */
  const avklaratAutomatiskt = useMemo(() => {
    const härlett: Record<string, string> = {
      ...avklarat.anledningPerUppgift,
      ...fakturaAvklarat,
    };

    // Deklarationen är markerad som inlämnad i guiden. Appen kan inte kontrollera det mot
    // Skatteverket — det finns inget API — men användarens egen markering väger tyngre än en
    // kryssruta i listan, och ska därför inte gå att kryssa bort här.
    if (taxering.aktuell && taxering.inlämnad && taxering.taxeringsår) {
      härlett[`fastighetsdeklaration-smahus-${taxering.taxeringsår}`] = 'Inlämnad';
    }

    return härlett;
  }, [avklarat, fakturaAvklarat, taxering]);

  const ärKlar = useCallback(
    (id: string) => Boolean(avklaratAutomatiskt[id]) || klara.includes(id),
    [avklaratAutomatiskt, klara]
  );

  const öppna = useMemo(() => poster.filter((p) => !ärKlar(p.id)), [poster, ärKlar]);

  const nästa = öppna[0];
  const antalFörsenade = öppna.filter((p) => p.förfallodag.isBefore(idag, 'day')).length;

  const ärMomsregistrerad = useMemo(() => {
    if (organisation?.organisationsTyp === 'PRIVATPERSON') return false;
    // Organisationens egen uppgift är facit när den finns: den är svaret någon gett i
    // inställningarna, och det är samma uppgift som styr bokföringen (se Momsregel i backend).
    // Gissningarna nedan är kvar för en organisation som hämtats utan fältet.
    if (typeof organisation?.momsregistrerad === 'boolean') {
      return organisation.momsregistrerad;
    }
    if (organisation?.momsregisteringsnummer) return true;
    // Näringsverksamhet antas vara momspliktig tills motsatsen visar sig — en missad
    // momsdeklaration kostar förseningsavgift, en onödig påminnelse kostar ingenting.
    if (
      organisation?.organisationsTyp === 'AKTIEBOLAG' ||
      organisation?.organisationsTyp === 'FÖRETAG' ||
      organisation?.organisationsTyp === 'ENSKILD_FIRMA'
    )
      return true;
    return false;
  }, [
    organisation?.organisationsTyp,
    organisation?.momsregisteringsnummer,
    organisation?.momsregistrerad,
  ]);

  return {
    idag,
    poster,
    öppna,
    nästa,
    klara,
    ärKlar,
    avklaratAutomatiskt,
    antalFörsenade,
    inställningar,
    uppdatera,
    växlaKlar,
    växlaTyp,
    uppdateraInställningar,
    momsperiod,
    ärArbetsgivare,
    arbetsgivarbedömning,
    ärMomsregistrerad,
    taxering,
    ärPrivatperson: organisation?.organisationsTyp === 'PRIVATPERSON',
    harRäkenskapsår: (organisation?.räkenskapsår?.length ?? 0) > 0,
  };
}
